/**
 * Component Style Utilities
 * Simplified utility functions for applying design system overrides to FluentUI components
 */

import type { FluentUIDesignKitTokens } from "./core/tokens";

/**
 * Button styling utility with size variants
 */
export const buttonStyle = (
  tokens: FluentUIDesignKitTokens,
  size: "small" | "normal" = "normal",
) => ({
  fontFamily: tokens.fontFamilyBase,
  fontSize: size === "small" ? tokens.fontSizeBase200 : tokens.fontSizeBase300,
  fontWeight: tokens.fontWeightMedium,
  lineHeight:
    size === "small" ? tokens.lineHeightBase200 : tokens.lineHeightBase300,
  borderRadius:
    size === "small" ? tokens.borderRadiusXXLarge : tokens.borderRadiusXXXLarge,
});

/**
 * Dropdown styling utility
 */
export const dropdownStyle = (tokens: FluentUIDesignKitTokens) => ({
  borderRadius: tokens.borderRadiusXXXLarge,
  height: "36px",
  minHeight: "36px",
  maxHeight: "36px",
});

export const inputStyle = (tokens: FluentUIDesignKitTokens) => ({
  borderRadius: tokens.borderRadiusXXXLarge,
  height: "36px",
  minHeight: "36px",
  maxHeight: "36px",
});

/**
 * SearchBox styling utility
 */
export const searchBoxStyle = (tokens: FluentUIDesignKitTokens) => ({
  borderRadius: tokens.borderRadiusXXXLarge,
  fontFamily: tokens.fontFamilyBase,
  fontSize: tokens.fontSizeBase300,
  lineHeight: tokens.lineHeightBase300,
  height: "36px",
  minHeight: "36px",
  maxHeight: "36px",
});

/**
 * Label styling utility
 */
export const labelStyle = (tokens: FluentUIDesignKitTokens) => ({
  fontFamily: tokens.fontFamilyBase,
  fontSize: tokens.fontSizeBase200,
  fontWeight: tokens.fontWeightRegular,
  lineHeight: tokens.lineHeightBase200,
});

/**
 * Tag/Badge styling utility
 */
export const tagStyle = (
  tokens: FluentUIDesignKitTokens,
  variant: "straight" | "rounded" = "rounded",
) => ({
  fontFamily: tokens.fontFamilyBase,
  fontSize: tokens.fontSizeBase200,
  fontWeight: tokens.fontWeightBold,
  lineHeight: tokens.lineHeightBase200,
  paddingInline: tokens.spacingHorizontalXS,
  paddingBlock: "0px",
  borderRadius:
    variant === "rounded"
      ? tokens.borderRadiusXXXXLarge
      : tokens.borderRadiusLarge,
  cursor: "pointer",
});

/**
 * Helper utility for design token access
 */
export const withDesignTokens = (tokens: FluentUIDesignKitTokens) => ({
  button: (size: "small" | "normal" = "normal") => buttonStyle(tokens, size),
  dropdown: () => dropdownStyle(tokens),
  searchBox: () => searchBoxStyle(tokens),
  label: () => labelStyle(tokens),
  input: () => inputStyle(tokens),
  tag: (variant: "straight" | "rounded" = "rounded") =>
    tagStyle(tokens, variant),
});
